import React from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { SUPPORTED_LANGUAGES } from '../../i18n';

interface Props {
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
}

// Maps the app's i18next language code to a BCP-47 locale for date formatting, so the header
// date (and anywhere else that reuses this) reads in the selected language's own weekday/month
// names — e.g. "jeu. 27 août 2026" in French — instead of always English regardless of choice.
const DATE_LOCALE: Record<string, string> = { en: 'en-KE', sw: 'sw-KE', fr: 'fr-FR' };

export default function Layout({ children, title, actions }: Props) {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALE[i18n.language] || 'en-KE';
  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-12 flex-shrink-0 flex items-center justify-between px-5 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-1.5 h-5 rounded-full flex-shrink-0" style={{background:'linear-gradient(180deg,#02357A,#039089)'}}/>
            {title ? (
              <span className="text-sm font-semibold text-gray-700 truncate">{title}</span>
            ) : (
              <span className="text-sm text-gray-400">Clinqov Meditrack HIS</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions && <div className="flex items-center gap-2">{actions}</div>}
            <div className="w-px h-4 bg-gray-200"/>
            <span className="text-xs text-gray-400 hidden sm:block">
              {new Date().toLocaleDateString(dateLocale,{weekday:'short',day:'numeric',month:'short',year:'numeric'})}
            </span>
            <div className="w-px h-4 bg-gray-200"/>
            {/* Language switcher — purely client-side (i18next-browser-languagedetector
                persists the choice under its own localStorage key), so it works the same on
                every screen without needing this deployment's backend settings to have loaded.
                See i18n/index.ts and utils/locale.ts for the country/currency counterpart. */}
            <select
              aria-label="Language"
              className="text-xs text-gray-500 border border-gray-200 rounded-md px-1.5 py-0.5 bg-white focus:outline-none"
              value={i18n.language?.split('-')[0] || 'en'}
              onChange={e => i18n.changeLanguage(e.target.value)}
            >
              {SUPPORTED_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <div className="w-px h-4 bg-gray-200"/>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
              <span className="text-xs text-gray-500">{t('common.online','Online')}</span>
            </div>
          </div>
        </header>
        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
